/**
 * PitchGradeCard - Letter grade visualization for pitch/player metrics
 * Inspired by PitcherList's grade cards (Stuff, Location, PLV, etc.)
 * Clean card with a large letter grade and metric value
 */
import React from 'react';
import { Box, Typography, alpha } from '@mui/material';

const GRADE_CONFIG = {
  'A+': { color: '#00C851', bg: '#00C851' },
  'A':  { color: '#4CAF50', bg: '#4CAF50' },
  'A-': { color: '#66BB6A', bg: '#66BB6A' },
  'B+': { color: '#8BC34A', bg: '#8BC34A' },
  'B':  { color: '#CDDC39', bg: '#CDDC39' },
  'B-': { color: '#FDD835', bg: '#FDD835' },
  'C+': { color: '#FFC107', bg: '#FFC107' },
  'C':  { color: '#FF9800', bg: '#FF9800' },
  'C-': { color: '#FF7043', bg: '#FF7043' },
  'D':  { color: '#F44336', bg: '#F44336' },
  'F':  { color: '#D32F2F', bg: '#D32F2F' },
};

/**
 * Calculate letter grade from a value using configurable thresholds
 * @param {number} value - The stat value to grade
 * @param {Array} thresholds - Array of {min, grade} sorted desc by min
 * @param {boolean} lowerIsBetter - True for stats like ERA where lower = better
 */
export const calculateGrade = (value, thresholds, lowerIsBetter = false) => {
  if (value == null || isNaN(value)) return 'N/A';
  
  const sorted = lowerIsBetter 
    ? [...thresholds].sort((a, b) => a.min - b.min) 
    : [...thresholds].sort((a, b) => b.min - a.min);
  
  for (const t of sorted) {
    if (lowerIsBetter ? value <= t.min : value >= t.min) return t.grade;
  }
  return sorted[sorted.length - 1]?.grade || 'F';
};

// Predefined thresholds for common stats
export const STAT_THRESHOLDS = {
  batting: {
    avg:  [{ min: .320, grade: 'A+' }, { min: .300, grade: 'A' }, { min: .280, grade: 'B+' }, { min: .265, grade: 'B' }, { min: .250, grade: 'C+' }, { min: .235, grade: 'C' }, { min: .220, grade: 'D' }, { min: 0, grade: 'F' }],
    obp:  [{ min: .400, grade: 'A+' }, { min: .370, grade: 'A' }, { min: .345, grade: 'B+' }, { min: .325, grade: 'B' }, { min: .310, grade: 'C+' }, { min: .290, grade: 'C' }, { min: .270, grade: 'D' }, { min: 0, grade: 'F' }],
    slg:  [{ min: .550, grade: 'A+' }, { min: .500, grade: 'A' }, { min: .450, grade: 'B+' }, { min: .420, grade: 'B' }, { min: .390, grade: 'C+' }, { min: .360, grade: 'C' }, { min: .320, grade: 'D' }, { min: 0, grade: 'F' }],
    ops:  [{ min: .950, grade: 'A+' }, { min: .870, grade: 'A' }, { min: .790, grade: 'B+' }, { min: .740, grade: 'B' }, { min: .700, grade: 'C+' }, { min: .660, grade: 'C' }, { min: .600, grade: 'D' }, { min: 0, grade: 'F' }],
    iso:  [{ min: .250, grade: 'A+' }, { min: .210, grade: 'A' }, { min: .170, grade: 'B+' }, { min: .140, grade: 'B' }, { min: .110, grade: 'C+' }, { min: .080, grade: 'C' }, { min: .050, grade: 'D' }, { min: 0, grade: 'F' }],
    woba: [{ min: .400, grade: 'A+' }, { min: .370, grade: 'A' }, { min: .340, grade: 'B+' }, { min: .320, grade: 'B' }, { min: .300, grade: 'C+' }, { min: .280, grade: 'C' }, { min: .260, grade: 'D' }, { min: 0, grade: 'F' }],
  },
  pitching: {
    era:    [{ min: 2.00, grade: 'A+' }, { min: 2.75, grade: 'A' }, { min: 3.25, grade: 'B+' }, { min: 3.75, grade: 'B' }, { min: 4.25, grade: 'C+' }, { min: 4.75, grade: 'C' }, { min: 5.50, grade: 'D' }, { min: 99, grade: 'F' }],
    whip:   [{ min: 0.90, grade: 'A+' }, { min: 1.05, grade: 'A' }, { min: 1.15, grade: 'B+' }, { min: 1.25, grade: 'B' }, { min: 1.35, grade: 'C+' }, { min: 1.45, grade: 'C' }, { min: 1.60, grade: 'D' }, { min: 99, grade: 'F' }],
    k9:     [{ min: 11.0, grade: 'A+' }, { min: 9.5, grade: 'A' }, { min: 8.5, grade: 'B+' }, { min: 7.5, grade: 'B' }, { min: 6.5, grade: 'C+' }, { min: 5.5, grade: 'C' }, { min: 4.5, grade: 'D' }, { min: 0, grade: 'F' }],
    bb9:    [{ min: 1.5, grade: 'A+' }, { min: 2.0, grade: 'A' }, { min: 2.5, grade: 'B+' }, { min: 3.0, grade: 'B' }, { min: 3.5, grade: 'C+' }, { min: 4.0, grade: 'C' }, { min: 5.0, grade: 'D' }, { min: 99, grade: 'F' }],
    fip:    [{ min: 2.50, grade: 'A+' }, { min: 3.00, grade: 'A' }, { min: 3.50, grade: 'B+' }, { min: 3.90, grade: 'B' }, { min: 4.30, grade: 'C+' }, { min: 4.80, grade: 'C' }, { min: 5.50, grade: 'D' }, { min: 99, grade: 'F' }],
    stuffPlus: [{ min: 130, grade: 'A+' }, { min: 120, grade: 'A' }, { min: 115, grade: 'A-' }, { min: 110, grade: 'B+' }, { min: 105, grade: 'B' }, { min: 100, grade: 'B-' }, { min: 95, grade: 'C+' }, { min: 90, grade: 'C' }, { min: 85, grade: 'C-' }, { min: 75, grade: 'D' }, { min: 0, grade: 'F' }],
  }
};

const PitchGradeCard = ({ 
  label, 
  grade, 
  value, 
  subtitle,
  size = 'medium',  // 'small' | 'medium' | 'large'
}) => {
  const config = GRADE_CONFIG[grade] || GRADE_CONFIG['C'];
  
  const sizes = {
    small:  { card: 64, grade: '1.1rem', label: '0.6rem', value: '0.55rem' },
    medium: { card: 80, grade: '1.4rem', label: '0.7rem', value: '0.65rem' },
    large:  { card: 100, grade: '1.8rem', label: '0.8rem', value: '0.75rem' },
  };
  const s = sizes[size] || sizes.medium;

  return (
    <Box sx={{
      width: s.card,
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.3,
    }}>
      {/* Grade circle */}
      <Box sx={{
        width: s.card * 0.7,
        height: s.card * 0.7,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(config.bg, 0.2)}, ${alpha(config.bg, 0.1)})`,
        border: `2px solid ${alpha(config.color, 0.5)}`,
      }}>
        <Typography sx={{
          fontWeight: 900,
          fontSize: s.grade,
          color: config.color,
          lineHeight: 1,
        }}>
          {grade || 'N/A'}
        </Typography>
      </Box>
      
      {/* Label */}
      <Typography variant="caption" sx={{ 
        fontWeight: 700, fontSize: s.label, color: 'text.primary', lineHeight: 1.1 
      }}>
        {label}
      </Typography>
      
      {/* Value */}
      {value !== undefined && (
        <Typography variant="caption" sx={{ 
          fontWeight: 500, fontSize: s.value, color: 'text.secondary', lineHeight: 1 
        }}>
          {value}
        </Typography>
      )}
      
      {subtitle && (
        <Typography variant="caption" sx={{ 
          fontWeight: 400, fontSize: '0.75rem', color: 'text.disabled', lineHeight: 1 
        }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export default PitchGradeCard;
